import * as Y from 'yjs';
import type { YjsRegistry, YjsRoomKind } from '@/lib/sync/yjs-registry';
import { useEntityStore, getEntityRow } from '@/lib/sync/entity-store';
import type { BaseEntityRow, CollectionRow, Op, PendingOp } from '@/lib/sync/entity-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { removeMyAccess } from '@/lib/sharing/api/access';
import { capture } from '@/lib/analytics/capture';
import type { EntityKind } from '@/lib/analytics/capture';
import type { RemoveAccessResult } from '@/lib/sharing/api/access';

export interface CreateDocOptions {
  title?: string;
  kind?: 'document' | 'canvas';
  collectionId?: string;
}

function makeOp(ops: PendingOp['ops']): PendingOp {
  return {
    opId: crypto.randomUUID(),
    ops,
    createdAt: Date.now(),
    sent: false,
  };
}

/**
 * BaseEntityRow mutators (rename / archive / addToCollection / addTag / …) all
 * follow the same shape: look up the row, build a wire op, optimistically
 * apply the patch with version+1 and a fresh updatedAt. This helper centralises
 * that shape so adding a new mutator means one method, not one method × the
 * same five lines of plumbing.
 *
 * `opFactory` receives the current version (so the wire op's `version` matches
 * what the server checks for OCC); `patch` is the per-method delta merged onto
 * the row in the optimistic UPSERTED event. Helper supplies version bump,
 * updatedAt, and the modelKind from `existing.kind`.
 *
 * Returns silently when the row is gone (already deleted locally) — same
 * fail-soft behaviour every per-method version had.
 */
function mutateEntity(
  id: string,
  opFactory: (version: number) => Op,
  patch: Partial<BaseEntityRow>,
  // Derived/cosmetic mutations (preview markup) must NOT advance updatedAt:
  // the preview is re-emitted on every open (initial Yjs hydration fires the
  // editor's update listener), so bumping updatedAt would sort the entity to
  // the top of "recently updated" for no real edit (PRD-16). The server
  // already declines to bump updated_at for an unchanged preview, so skipping
  // the optimistic bump keeps the client consistent with the server.
  { touchUpdatedAt = true }: { touchUpdatedAt?: boolean } = {},
): void {
  const existing = getEntityRow(useEntityStore.getState(), id);
  if (!existing) return;
  const version = existing.version ?? 0;

  useEntityStore.getState().addOp(makeOp([opFactory(version)]));

  useEntityStore.getState().applyEvent({
    id: '', kind: 'UPSERTED', modelKind: existing.kind, modelId: id,
    data: {
      ...existing,
      ...patch,
      version: version + 1,
      ...(touchUpdatedAt ? { updatedAt: new Date().toISOString() } : {}),
    } as unknown as Record<string, unknown>,
  });
}

export class WorkspaceService {
  constructor(private readonly registry: YjsRegistry) {}

  async createDoc(opts: CreateDocOptions = {}): Promise<{ id: string; doc: Y.Doc }> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const kind = opts.kind ?? 'document';
    const title = opts.title ?? 'Untitled';

    const doc = await this.registry.subscribe(id, kind as YjsRoomKind);

    doc.transact(() => {
      const meta = doc.getMap('metadata');
      meta.set('id', id);
      meta.set('documentType', kind);
      meta.set('createdAt', now);
    });

    // New doc — no server state to wait for; mark synced immediately so the
    // editor doesn't stall on the "Not available offline" gate.
    this.registry.markServerSynced(id);

    const collectionIds = opts.collectionId ? [opts.collectionId] : [];

    const ops: PendingOp['ops'] = [{ action: 'create', kind, entityId: id, title }];
    if (collectionIds.length > 0) {
      ops.push({ action: 'setCollectionIds', entityId: id, collectionIds, version: 0 });
    }
    useEntityStore.getState().addOp(makeOp(ops));

    const entityMeta: BaseEntityRow = {
      id,
      title,
      kind,
      createdAt: now,
      createdBy: null,
      updatedAt: now,
      isArchived: false,
      snippet: '',
      previewMarkup: null,
      previewKind: null,
      tags: [],
      version: 0,
      collectionIds,
      access: { permission: 'owner', grantedAt: now, grantedBy: null, via: 'direct', capabilities: { view: true, edit: true, manage: true, delete: true }, removeSelf: false, removeSelfItemCount: null },
    };
    useEntityStore.getState().applyEvent({
      id: '', kind: 'UPSERTED', modelKind: kind, modelId: id,
      data: entityMeta as unknown as Record<string, unknown>,
    });

    capture('entity_created', { entity_kind: kind as EntityKind });

    return { id, doc };
  }

  updatePreviewMarkup(id: string, markup: string, kind: 'html' | 'svg'): void {
    mutateEntity(
      id,
      (version) => ({ action: 'updatePreview', entityId: id, markup, kind, version }),
      { previewMarkup: markup, previewKind: kind },
      { touchUpdatedAt: false },
    );
  }

  renameDoc(id: string, title: string): void {
    mutateEntity(
      id,
      (version) => ({ action: 'rename', entityId: id, title, version }),
      { title },
    );
  }

  archiveDoc(id: string, archived: boolean): void {
    mutateEntity(
      id,
      (version) => ({ action: 'archive', entityId: id, archived, version }),
      { isArchived: archived },
    );
  }

  async deleteDocPermanently(id: string): Promise<void> {
    const existing = getEntityRow(useEntityStore.getState(), id);
    const version = existing?.version ?? 0;
    // Default to 'document' when the row is gone (already-removed local state);
    // server will reject the op idempotently if the entity is also gone there.
    const modelKind = existing?.kind ?? 'document';

    useEntityStore.getState().addOp(makeOp([{
      action: 'delete',
      entityId: id,
      version,
    }]));

    useEntityStore.getState().applyEvent({
      id: '', kind: 'DELETED', modelKind, modelId: id, data: null,
    });

    await this.registry.deletePermanently(id);
  }

  /**
   * Remove this user's own access to a shared entity.
   *
   * Deliberately does NOT enqueue a delete op. `deleteDocPermanently` does,
   * because it destroys the entity for the owner and every collaborator;
   * leaving must only drop the caller's own grant. The server emits DELETED
   * to this user alone.
   *
   * The server call comes first so local state is only cleared once the
   * removal is known to have happened — a failed request must leave the item
   * (and its offline copy) in place. 'already-gone' is a success: the grant is
   * gone either way, so local cleanup still runs.
   */
  async leaveEntity(id: string): Promise<RemoveAccessResult> {
    const result = await removeMyAccess(id);

    const modelKind = getEntityRow(useEntityStore.getState(), id)?.kind ?? 'document';

    useEntityStore.getState().applyEvent({
      id: '', kind: 'DELETED', modelKind, modelId: id, data: null,
    });

    await this.registry.deletePermanently(id);

    return result;
  }

  async restoreDoc(
    id: string,
    metadata: { title: string; kind: 'document' | 'canvas' },
  ): Promise<void> {
    const now = new Date().toISOString();
    const existing = getEntityRow(useEntityStore.getState(), id);

    // Use the existing row when present (preserves tags, collectionIds, etc.);
    // otherwise build a minimal placeholder shape from the caller's metadata.
    // The placeholder has BaseEntityRow shape — passes isBaseEntityRow guard.
    const data = existing
      ? { ...existing, isArchived: false, updatedAt: now }
      : {
          id,
          kind: metadata.kind,
          title: metadata.title,
          snippet: '',
          tags: [],
          isArchived: false,
          collectionIds: [],
          previewMarkup: null,
          previewKind: null,
          version: 0,
          createdAt: now,
          updatedAt: now,
          createdBy: useAuthStore.getState().user?.sub ?? null,
          access: { permission: 'owner' as const, grantedAt: now, grantedBy: null, via: 'direct' as const },
        };

    useEntityStore.getState().addOp(makeOp([{
      action: 'restore',
      entityId: id,
      version: existing?.version ?? 0,
    }]));

    useEntityStore.getState().applyEvent({
      id: '', kind: 'UPSERTED', modelKind: metadata.kind, modelId: id,
      data: data as unknown as Record<string, unknown>,
    });

    await this.registry.subscribe(id, metadata.kind as YjsRoomKind);
  }

  createCollection(name: string): string {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();

    useEntityStore.getState().addOp(makeOp([{
      action: 'create',
      kind: 'collection',
      entityId: id,
      title: name,
    }]));

    const collection: CollectionRow = {
      id,
      kind: 'collection',
      title: name,
      snippet: '',
      isArchived: false,
      tags: [],
      collectionIds: [],
      previewMarkup: null,
      previewKind: null,
      version: 0,
      createdAt: now,
      updatedAt: now,
      createdBy: useAuthStore.getState().user?.sub ?? null,
      access: { permission: 'owner', grantedAt: now, grantedBy: null, via: 'direct', capabilities: { view: true, edit: true, manage: true, delete: true }, removeSelf: false, removeSelfItemCount: null },
    };
    useEntityStore.getState().applyEvent({
      id: '', kind: 'UPSERTED', modelKind: 'collection', modelId: id,
      data: collection as unknown as Record<string, unknown>,
    });

    return id;
  }

  renameCollection(id: string, name: string): void {
    const existing = useEntityStore.getState().collections.get(id);
    if (!existing) return;
    const now = new Date().toISOString();
    const version = existing.version ?? 0;

    useEntityStore.getState().addOp(makeOp([{
      action: 'rename',
      entityId: id,
      title: name,
      version,
    }]));

    useEntityStore.getState().applyEvent({
      id: '', kind: 'UPSERTED', modelKind: 'collection', modelId: id,
      data: { ...existing, title: name, version: version + 1, updatedAt: now } as unknown as Record<string, unknown>,
    });
  }

  archiveCollection(id: string, archived: boolean): void {
    const existing = useEntityStore.getState().collections.get(id);
    if (!existing) return;
    const now = new Date().toISOString();
    const version = existing.version ?? 0;

    useEntityStore.getState().addOp(makeOp([{
      action: 'archive',
      entityId: id,
      archived,
      version,
    }]));

    useEntityStore.getState().applyEvent({
      id: '', kind: 'UPSERTED', modelKind: 'collection', modelId: id,
      data: { ...existing, isArchived: archived, version: version + 1, updatedAt: now } as unknown as Record<string, unknown>,
    });
  }

  async deleteCollectionPermanently(id: string): Promise<void> {
    const existing = useEntityStore.getState().collections.get(id);
    const version = existing?.version ?? 0;

    useEntityStore.getState().addOp(makeOp([{
      action: 'delete',
      entityId: id,
      version,
    }]));

    useEntityStore.getState().applyEvent({
      id: '', kind: 'DELETED', modelKind: 'collection', modelId: id, data: null,
    });

    await this.registry.deletePermanently(id);
  }

  addToCollection(entityId: string, collectionId: string): void {
    const existing = getEntityRow(useEntityStore.getState(), entityId);
    if (!existing || existing.collectionIds.includes(collectionId)) return;
    const collectionIds = [...existing.collectionIds, collectionId];
    mutateEntity(
      entityId,
      (version) => ({ action: 'setCollectionIds', entityId, collectionIds, version }),
      { collectionIds },
    );
  }

  removeFromCollection(entityId: string, collectionId: string): void {
    const existing = getEntityRow(useEntityStore.getState(), entityId);
    if (!existing) return;
    const collectionIds = existing.collectionIds.filter(id => id !== collectionId);
    mutateEntity(
      entityId,
      (version) => ({ action: 'setCollectionIds', entityId, collectionIds, version }),
      { collectionIds },
    );
  }

  addTag(entityId: string, tag: string): void {
    const existing = getEntityRow(useEntityStore.getState(), entityId);
    if (!existing || existing.tags.includes(tag)) return;
    const tags = [...existing.tags, tag];
    mutateEntity(
      entityId,
      (version) => ({ action: 'setTags', entityId, tags, version }),
      { tags },
    );
  }

  removeTag(entityId: string, tag: string): void {
    const existing = getEntityRow(useEntityStore.getState(), entityId);
    if (!existing) return;
    const tags = existing.tags.filter(t => t !== tag);
    mutateEntity(
      entityId,
      (version) => ({ action: 'setTags', entityId, tags, version }),
      { tags },
    );
  }
}
